/* eslint-disable no-cond-assign */
import { FC, useMemo } from 'react';
import { ChatMessage } from '../types';
import ActionCard from './ActionCard';
import StreamingIndicator from './StreamingIndicator';
import { cleanRecommendationMarkers } from '../utils/parsers';

interface MessageItemProps {
  message: ChatMessage;
  onNavigate?: () => void;
}

const MessageItem: FC<MessageItemProps> = ({ message, onNavigate }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isTool = message.role === 'tool';

  // Clean content for display (remove recommendation markers)
  const displayContent = useMemo(() => {
    if (!message.content) return '';
    return cleanRecommendationMarkers(message.content);
  }, [message.content]);

  // Format content with basic markdown support
  const formattedContent = useMemo(() => {
    if (!displayContent) return null;

    // Simple markdown parsing for common patterns
    const lines = displayContent.split('\n');
    const elements: JSX.Element[] = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Headers
      if (trimmed.startsWith('### ')) {
        elements.push(
          <h4 key={index} className="font-semibold text-sm mt-2 mb-1 text-inherit">
            {trimmed.slice(4)}
          </h4>,
        );
        return;
      }
      if (trimmed.startsWith('## ')) {
        elements.push(
          <h3 key={index} className="font-semibold text-base mt-2 mb-1 text-inherit">
            {trimmed.slice(3)}
          </h3>,
        );
        return;
      }

      // Bullet points
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        elements.push(
          <li key={index} className="ml-4 text-sm text-inherit">
            {formatInlineMarkdown(trimmed.slice(2))}
          </li>,
        );
        return;
      }

      // Numbered lists
      const numberedMatch = trimmed.match(/^(\d+)\.\s(.+)/);
      if (numberedMatch) {
        elements.push(
          <li key={index} className="ml-4 text-sm list-decimal text-inherit">
            {formatInlineMarkdown(numberedMatch[2])}
          </li>,
        );
        return;
      }

      // Empty lines
      if (!trimmed) {
        elements.push(<div key={index} className="h-2" />);
        return;
      }

      // Regular paragraphs
      elements.push(
        <p key={index} className="text-sm text-inherit leading-relaxed">
          {formatInlineMarkdown(trimmed)}
        </p>,
      );
    });

    return elements;
  }, [displayContent]);

  // Don't render tool messages directly
  if (isTool) {
    return null;
  }

  // Tool execution indicator
  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0;
  const isExecutingTools = hasToolCalls && message.isStreaming;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
          isUser
            ? 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white rounded-br-sm'
            : 'bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 rounded-bl-sm border border-gray-100 dark:border-slate-700'
        }`}
      >
        {/* Message content - show loading indicator while streaming to prevent flash */}
        {isAssistant && message.isStreaming ? (
          <StreamingIndicator text={isExecutingTools ? 'Fetching data' : undefined} />
        ) : (
          <>
            {/* Tool call badges (when not streaming) */}
            {hasToolCalls && !message.isStreaming && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {message.toolCalls?.map((tc) => (
                  <span
                    key={tc.id}
                    className="inline-flex items-center px-2 py-1 rounded-lg text-xs bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 font-medium"
                  >
                    <svg
                      className="w-3 h-3 mr-1.5 text-orange dark:text-teal-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    {formatToolName(tc.name)}
                  </span>
                ))}
              </div>
            )}

            {/* Message content - only shown after streaming completes */}
            <div className="space-y-1.5">{formattedContent}</div>
          </>
        )}

        {/* Recommendations */}
        {isAssistant && message.recommendations && message.recommendations.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
            {message.recommendations.map((rec, index) => (
              <ActionCard key={index} recommendation={rec} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to format inline markdown (bold, italic, code)
const formatInlineMarkdown = (text: string): React.ReactNode => {
  // Simple regex-based formatting
  const parts: React.ReactNode[] = [];
  const remaining = text;
  let keyIndex = 0;

  // Bold: **text**
  const boldRegex = /\*\*([^*]+)\*\*/g;
  // Italic: *text* or _text_
  const italicRegex = /(?:\*([^*]+)\*|_([^_]+)_)/g;
  // Code: `text`
  const codeRegex = /`([^`]+)`/g;

  // Replace patterns with React elements
  let match;
  let lastIndex = 0;

  // Process bold
  const boldParts: Array<{ start: number; end: number; content: React.ReactNode }> = [];
  while ((match = boldRegex.exec(text))) {
    boldParts.push({
      start: match.index,
      end: match.index + match[0].length,
      content: (
        <strong key={`bold-${keyIndex++}`} className="font-semibold">
          {match[1]}
        </strong>
      ),
    });
  }

  // Process code
  const codeParts: Array<{ start: number; end: number; content: React.ReactNode }> = [];
  while ((match = codeRegex.exec(text))) {
    codeParts.push({
      start: match.index,
      end: match.index + match[0].length,
      content: (
        <code
          key={`code-${keyIndex++}`}
          className="px-1.5 py-0.5 bg-gray-200 dark:bg-slate-600 rounded text-xs font-mono"
        >
          {match[1]}
        </code>
      ),
    });
  }

  // Combine and sort all replacements
  const allParts = [...boldParts, ...codeParts].sort((a, b) => a.start - b.start);

  if (allParts.length === 0) {
    return text;
  }

  // Build final output
  lastIndex = 0;
  for (const part of allParts) {
    if (part.start > lastIndex) {
      parts.push(remaining.slice(lastIndex, part.start));
    }
    parts.push(part.content);
    lastIndex = part.end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
};

// Format tool name for display
const formatToolName = (name: string): string => {
  return name
    .replace(/_/g, ' ')
    .replace(/^get /, '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default MessageItem;
